import { useState, useEffect, useRef, useCallback } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import MentionTextarea from "@/components/MentionTextarea";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import { toast } from "sonner";
import SharedNavigation from "@/components/SharedNavigation";
import BackButton from "@/components/BackButton";
import PresenceDot from "@/components/PresenceDot";
import InviteArtistsDialog from "@/components/InviteArtistsDialog";
import { getProfilePhotoUrl, getCoverUrl, getPlaylistCoverUrl, optimizeImage } from "@/lib/mediaUrls";
import { getPublicAppUrl, getPublicShareUrl } from "@/lib/publicUrl";
import CrewModerationModal from "@/components/CrewModerationModal";
import { stashReferral } from "@/lib/referralStash";

const QUICK_REACTIONS = ['🔥', '🙌', '❤️', '🎧', '💯'];

// Phase 15.5 P3 — Crew Privacy: render founder/admin/owner/mod/seeder badges
// alongside crew members and post authors so the hierarchy is always visible.
const CrewMemberBadges = ({ memberBadges = [], isCreator = false, isAdmin = false, userId = '' }) => {
  const badges = Array.isArray(memberBadges) ? memberBadges : [];
  const chips = [];
  if (badges.includes('founder')) {
    chips.push({ key: 'founder', icon: '👑', title: 'RGM Founder', cls: 'bg-amber-500/20 text-amber-200 border-amber-500/40' });
  }
  if (badges.includes('admin')) {
    chips.push({ key: 'admin', icon: '⚡', title: 'RGM Admin', cls: 'bg-purple-500/20 text-purple-200 border-purple-500/40' });
  }
  if (badges.includes('ar')) {
    chips.push({ key: 'ar', icon: '🎯', title: 'RGM A&R', cls: 'bg-pink-500/20 text-pink-200 border-pink-500/40' });
  }
  if (badges.includes('verified_artist')) {
    chips.push({ key: 'verified', icon: '✓', title: 'Verified Artist', cls: 'bg-sky-500/20 text-sky-200 border-sky-500/40' });
  }
  // Crew-scoped roles take precedence visually
  if (isCreator) {
    chips.push({ key: 'creator', icon: '👑', title: 'Crew Owner', cls: 'bg-yellow-500/20 text-yellow-200 border-yellow-500/40' });
  } else if (isAdmin) {
    chips.push({ key: 'mod', icon: '🛡️', title: 'Crew Moderator', cls: 'bg-indigo-500/20 text-indigo-200 border-indigo-500/40' });
  }
  if (badges.includes('crew_seeder')) {
    chips.push({ key: 'seeder', icon: '🌱', title: 'Crew Seeder — brought 3+ artists in', cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40' });
  }
  if (chips.length === 0) return null;
  return (
    <span className="inline-flex items-center gap-1 flex-shrink-0" data-testid={`crew-member-badges-${userId}`}>
      {chips.slice(0, 4).map((c) => (
        <span
          key={c.key}
          title={c.title}
          className={`text-[10px] leading-none w-5 h-5 rounded-full border flex items-center justify-center ${c.cls}`}
          data-testid={`crew-badge-${c.key}-${userId}`}
        >
          {c.icon}
        </span>
      ))}
    </span>
  );
};

const CrewRequestsPanel = ({ crewId, onChange }) => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [acting, setActing] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/crews/${crewId}/requests`);
      setRequests(r.data.requests || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not load requests');
    } finally {
      setLoading(false);
    }
  }, [crewId]);

  useEffect(() => { load(); }, [load]);

  const act = async (requestId, action) => {
    setActing((s) => ({ ...s, [requestId]: action }));
    try {
      await api.post(`/crews/${crewId}/requests/${requestId}/${action}`);
      toast.success(action === 'approve' ? 'Request approved' : 'Request rejected');
      setRequests((prev) => prev.filter((r) => r.request_id !== requestId));
      onChange && onChange();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${action}`);
      setActing((s) => ({ ...s, [requestId]: null }));
    }
  };

  if (loading) {
    return (
      <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="crew-requests-loading">
        <p className="text-sm text-gray-400">Loading requests…</p>
      </Card>
    );
  }

  if (requests.length === 0) {
    return (
      <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="crew-requests-empty">
        <h3 className="text-white font-bold mb-1 flex items-center gap-2">
          <span>🛎️</span> Join Requests
        </h3>
        <p className="text-xs text-gray-500">No pending requests.</p>
      </Card>
    );
  }

  return (
    <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="crew-requests-panel">
      <h3 className="text-white font-bold mb-3 flex items-center gap-2">
        <span>🛎️</span> Join Requests ({requests.length})
      </h3>
      <div className="space-y-2">
        {requests.map((req) => {
          const a = acting[req.request_id];
          return (
            <div
              key={req.request_id}
              className="flex items-center gap-3 p-2 rounded-lg bg-black/30 border border-gray-800"
              data-testid={`crew-request-${req.request_id}`}
            >
              <img
                src={getProfilePhotoUrl(req.user || {})}
                alt={req.user?.artist_name}
                className="w-10 h-10 rounded-full object-cover"
              />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-medium truncate">{req.user?.artist_name}</p>
                <p className="text-[11px] text-gray-500">wants to join</p>
              </div>
              <Button
                size="sm"
                onClick={() => act(req.request_id, 'approve')}
                disabled={!!a}
                className="bg-green-600 hover:bg-green-500 text-white"
                data-testid={`approve-request-${req.request_id}`}
              >
                {a === 'approve' ? '…' : 'Approve'}
              </Button>
              <Button
                size="sm"
                onClick={() => act(req.request_id, 'reject')}
                disabled={!!a}
                variant="outline"
                className="text-red-300 border-red-500/40 hover:bg-red-500/20"
                data-testid={`reject-request-${req.request_id}`}
              >
                {a === 'reject' ? '…' : 'Reject'}
              </Button>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

const formatTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return iso;
  }
};

const SharedTrackPreview = ({ track }) => {
  if (!track) return null;
  return (
    <Link
      to={`/track/${track.submission_id}`}
      className="mt-2 flex items-center gap-3 p-2 rounded-lg bg-black/30 border border-gray-800 hover:border-purple-500/40 transition-colors"
      data-testid={`shared-track-${track.submission_id}`}
    >
      <img
        src={getCoverUrl(track)}
        alt={track.track_title}
        className="w-12 h-12 rounded object-cover"
        onError={(e) => { if (!e.target.dataset.fellback) { e.target.dataset.fellback = "1"; e.target.src = "/rgm-logo.png"; } }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-white text-sm font-medium truncate">🎵 {track.track_title}</p>
        <p className="text-gray-400 text-xs truncate">{track.artist_name}</p>
      </div>
      <span className="text-purple-300 text-xs">Open →</span>
    </Link>
  );
};

// Phase 19.0 — RGM Report card inside community posts. Tap to open report.
const SharedReportPreview = ({ report }) => {
  if (!report) return null;
  return (
    <Link
      to={`/reports/${report.report_id}`}
      className="mt-2 flex items-start gap-3 p-3 rounded-lg bg-gradient-to-br from-blue-900/40 to-black border border-blue-500/40 hover:border-blue-400/70 transition-colors"
      data-testid={`shared-report-${report.report_id}`}
    >
      <div className="text-2xl select-none flex-shrink-0" aria-hidden>📰</div>
      <div className="flex-1 min-w-0">
        <p className="text-blue-200 text-[10px] uppercase tracking-wider font-bold mb-0.5">
          RGM Report {report.category ? `· ${report.category}` : ''}
        </p>
        <p className="text-white text-sm font-bold truncate">{report.title}</p>
        {report.summary && (
          <p className="text-gray-300 text-xs mt-1 line-clamp-2 break-words">{report.summary}</p>
        )}
      </div>
      <span className="text-blue-300 text-xs whitespace-nowrap flex-shrink-0">Read →</span>
    </Link>
  );
};

// Phase 19.0 — Beat marketplace card inside community posts. Tap to open beat.
const SharedBeatPreview = ({ beat }) => {
  if (!beat) return null;
  const price = Number(beat.price || 0);
  const priceLabel = beat.is_free || price <= 0 ? 'Free' : `$${price.toFixed(2)}`;
  return (
    <Link
      to={`/beats/${beat.beat_id}`}
      className="mt-2 flex items-center gap-3 p-2 rounded-lg bg-black/30 border border-amber-500/30 hover:border-amber-400/70 transition-colors"
      data-testid={`shared-beat-${beat.beat_id}`}
    >
      <img
        src={optimizeImage(beat.cover_url || '/rgm-logo.png', 'card')}
        alt={beat.title}
        className="w-14 h-14 rounded object-cover border border-amber-500/30"
        onError={(e) => { if (!e.target.dataset.fellback) { e.target.dataset.fellback = "1"; e.target.src = "/rgm-logo.png"; } }}
      />
      <div className="flex-1 min-w-0">
        <p className="text-amber-200 text-[10px] uppercase tracking-wider font-bold">🎹 Beat</p>
        <p className="text-white text-sm font-bold truncate">{beat.title}</p>
        <p className="text-gray-400 text-xs truncate">
          {beat.producer_name || 'Producer'}{beat.bpm ? ` · ${beat.bpm} BPM` : ''}{beat.genre ? ` · ${beat.genre}` : ''}
        </p>
      </div>
      <span className="text-amber-300 text-xs font-bold whitespace-nowrap">{priceLabel} →</span>
    </Link>
  );
};

const SharedClubPreview = ({ club }) => {
  // 🎟️ Club Launch card — rendered inside crew posts when an artist
  // creates a club. Includes the club name, price, short description, and
  // a strong "Join Club" CTA that deep-links to /clubs/{id}.
  if (!club) return null;
  const price = Number(club.price_monthly || 0);
  const currency = (club.price_currency || "USD").toUpperCase();
  const priceLabel = price > 0 ? `${currency} ${price.toFixed(2)}/mo` : "Free";
  const desc = (club.description || "").trim();
  const shortDesc = desc.length > 140 ? `${desc.slice(0, 140)}…` : desc;
  return (
    <div
      className="mt-2 p-3 rounded-lg bg-gradient-to-br from-purple-900/40 via-pink-900/30 to-black border border-purple-500/40"
      data-testid={`shared-club-${club.club_id}`}
    >
      <div className="flex items-start gap-3">
        <div className="text-3xl select-none" aria-hidden>🎟️</div>
        <div className="flex-1 min-w-0">
          <p className="text-white text-sm font-bold truncate">{club.name}</p>
          <p className="text-pink-200 text-xs mt-0.5">
            <span className="inline-block px-1.5 py-0.5 rounded-full bg-pink-500/20 border border-pink-500/30 text-[10px] font-semibold">
              {priceLabel}
            </span>
            {typeof club.subscriber_count === "number" && club.subscriber_count > 0 && (
              <span className="ml-2 text-gray-400">
                {club.subscriber_count}{" "}
                {club.subscriber_count === 1 ? "subscriber" : "subscribers"}
              </span>
            )}
          </p>
          {shortDesc && (
            <p className="text-gray-300 text-sm mt-2 break-words whitespace-pre-wrap line-clamp-3">
              {shortDesc}
            </p>
          )}
        </div>
      </div>
      <Link
        to={`/clubs/${club.club_id}`}
        className="mt-3 inline-flex w-full sm:w-auto items-center justify-center gap-1.5 px-4 py-2 rounded-lg bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white text-sm font-bold transition-colors"
        data-testid={`join-club-cta-${club.club_id}`}
      >
        Join Club →
      </Link>
    </div>
  );
};

const PostBlock = ({ post, currentUserId, isMember, onReact, onReply, onDelete, onRestore, crewMeta = {} }) => {
  const [showReplyBox, setShowReplyBox] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [replyMentions, setReplyMentions] = useState([]);
  const [posting, setPosting] = useState(false);

  const submitReply = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      await onReply(post.post_id, replyText.trim(), replyMentions);
      setReplyText('');
      setReplyMentions([]);
      setShowReplyBox(false);
    } finally {
      setPosting(false);
    }
  };

  // iter_200 — Tombstone render for soft-deleted posts.
  const isDeleted = !!post.is_deleted;
  const isModeratorTombstone = isDeleted && post.deleted_by_role && post.deleted_by_role !== 'author';

  // iter_200 — Moderator/owner/author can trigger delete via UI.
  const canDelete = onDelete && !isDeleted && (
    post.author?.user_id === currentUserId
    || currentUserId === crewMeta.creatorId
    || (crewMeta.adminIds || []).includes(currentUserId)
    || crewMeta.isPlatformAdmin
  );

  // iter_203 — Restore is offered ONLY on moderator-tombstones to
  // authorized moderators. Author-self-deletes are respected.
  const canRestore = onRestore && isModeratorTombstone && (
    currentUserId === crewMeta.creatorId
    || (crewMeta.adminIds || []).includes(currentUserId)
    || crewMeta.isPlatformAdmin
  );

  return (
    <div
      className={`p-4 border rounded-lg ${
        isDeleted
          ? 'bg-gray-900/20 border-gray-800 opacity-70'
          : post.is_announcement
          ? 'bg-gradient-to-r from-fuchsia-900/30 to-pink-900/20 border-fuchsia-500/40 shadow-[0_0_20px_rgba(217,70,239,0.15)]'
          : 'bg-gray-900/40 border-gray-800'
      }`}
      data-testid={`post-${post.post_id}`}
    >
      {isDeleted && (
        <div
          className="text-xs italic text-gray-500 mb-2 flex items-center gap-2 flex-wrap"
          data-testid={`post-tombstone-${post.post_id}`}
        >
          <span>🚫</span>
          <span>{post.content || 'Message removed by moderator'}</span>
          {canRestore && (
            <button
              type="button"
              onClick={() => onRestore(post.post_id)}
              className="ml-auto text-[11px] font-black text-emerald-300 hover:text-emerald-200 border border-emerald-500/40 bg-emerald-500/10 hover:bg-emerald-500/20 rounded-full px-2 py-0.5"
              data-testid={`post-restore-${post.post_id}`}
            >
              ↩ Restore
            </button>
          )}
        </div>
      )}
      {!isDeleted && post.is_announcement && (
        <div
          className="flex items-center gap-2 mb-2 text-xs font-semibold text-fuchsia-300 uppercase tracking-wide"
          data-testid={`announcement-badge-${post.post_id}`}
        >
          <span>📣</span>
          <span>Announcement</span>
        </div>
      )}
      <div className="flex items-start gap-3">
        <Link to={`/artist/${post.author?.user_id}`}>
          <img
            src={getProfilePhotoUrl(post.author || {})}
            alt={post.author?.artist_name || 'Artist'}
            className="w-10 h-10 rounded-full object-cover"
          />
        </Link>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <Link
              to={`/artist/${post.author?.user_id}`}
              className="text-white font-medium hover:text-purple-300"
            >
              {post.author?.artist_name || 'Artist'}
            </Link>
            <CrewMemberBadges
              memberBadges={post.author?.badges || []}
              isCreator={post.author?.user_id === crewMeta.creatorId}
              isAdmin={(crewMeta.adminIds || []).includes(post.author?.user_id)}
              userId={`post-${post.post_id}-${post.author?.user_id}`}
            />
            <span className="text-xs text-gray-500 ml-auto">{formatTime(post.created_at)}</span>
          </div>
          {!isDeleted && post.content && (
            <p className="text-gray-200 mt-1 whitespace-pre-wrap break-words">{post.content}</p>
          )}
          {!isDeleted && post.image_url && (
            <img
              src={optimizeImage(post.image_url, 'card')}
              alt="post attachment"
              className="mt-2 max-h-96 w-auto rounded-lg border border-gray-800 object-contain bg-black"
              data-testid={`post-image-${post.post_id}`}
            />
          )}
          {!isDeleted && post.shared_track && <SharedTrackPreview track={post.shared_track} />}
          {!isDeleted && post.shared_club && <SharedClubPreview club={post.shared_club} />}
          {!isDeleted && post.shared_report && <SharedReportPreview report={post.shared_report} />}
          {!isDeleted && post.shared_beat && <SharedBeatPreview beat={post.shared_beat} />}

          {/* Reactions */}
          {!isDeleted && (
          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {Object.entries(post.reactions || {}).map(([emoji, users]) => {
              const reacted = (users || []).includes(currentUserId);
              return (
                <button
                  key={emoji}
                  onClick={() => isMember && onReact(post.post_id, emoji)}
                  disabled={!isMember}
                  className={`text-xs px-2 py-1 rounded-full border transition-colors ${
                    reacted
                      ? 'bg-purple-600/40 border-purple-400 text-white'
                      : 'bg-black/30 border-gray-700 text-gray-300 hover:border-purple-500/60'
                  } ${!isMember ? 'opacity-60 cursor-not-allowed' : ''}`}
                  data-testid={`reaction-${post.post_id}-${emoji}`}
                >
                  {emoji} {(users || []).length}
                </button>
              );
            })}
            {isMember && (
              <div className="flex items-center gap-1 ml-1">
                {QUICK_REACTIONS.map((e) => (
                  <button
                    key={e}
                    onClick={() => onReact(post.post_id, e)}
                    className="text-xs w-7 h-7 rounded-full hover:bg-purple-600/30 transition-colors"
                    title={`React with ${e}`}
                    data-testid={`quick-react-${post.post_id}-${e}`}
                  >
                    {e}
                  </button>
                ))}
              </div>
            )}
          </div>
          )}

          {/* Replies */}
          {post.replies && post.replies.length > 0 && (
            <div className="mt-3 pl-4 border-l-2 border-purple-500/30 space-y-2">
              {post.replies.map((r) => (
                <div
                  key={r.post_id}
                  className={`flex items-start gap-2 text-sm ${r.is_deleted ? 'opacity-60' : ''}`}
                  data-testid={`reply-${r.post_id}`}
                >
                  <Link to={`/artist/${r.author?.user_id}`}>
                    <img
                      src={getProfilePhotoUrl(r.author || {})}
                      alt={r.author?.artist_name}
                      className="w-7 h-7 rounded-full object-cover"
                    />
                  </Link>
                  <div className="flex-1">
                    <Link
                      to={`/artist/${r.author?.user_id}`}
                      className="text-white text-xs font-medium hover:text-purple-300"
                    >
                      {r.author?.artist_name || 'Artist'}
                    </Link>
                    <span className="text-gray-500 text-xs ml-2">{formatTime(r.created_at)}</span>
                    {r.is_deleted ? (
                      <p
                        className="text-gray-500 italic text-xs whitespace-pre-wrap break-words"
                        data-testid={`reply-tombstone-${r.post_id}`}
                      >
                        🚫 {r.content || 'Message removed by moderator'}
                      </p>
                    ) : (
                      <p className="text-gray-200 whitespace-pre-wrap break-words">{r.content}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isDeleted && (
          <div className="mt-2 flex items-center gap-3 text-xs text-gray-400">
            {isMember && (
              <button
                onClick={() => setShowReplyBox((s) => !s)}
                className="hover:text-white"
                data-testid={`reply-toggle-${post.post_id}`}
              >
                💬 Reply
              </button>
            )}
            {canDelete && (
              <button
                onClick={() => onDelete(post.post_id)}
                className="hover:text-red-400"
                data-testid={`delete-post-${post.post_id}`}
              >
                Delete
              </button>
            )}
          </div>
          )}

          {showReplyBox && isMember && (
            <div className="mt-2 flex gap-2">
              <div className="flex-1">
                <MentionTextarea
                  value={replyText}
                  onChange={setReplyText}
                  onMentionsChange={setReplyMentions}
                  rows={2}
                  placeholder="Write a reply… type @ to mention"
                  className="bg-black/30 text-white border-gray-700 text-sm w-full"
                  testIdPrefix={`reply-input-${post.post_id}`}
                />
              </div>
              <Button
                size="sm"
                onClick={submitReply}
                disabled={posting || !replyText.trim()}
                className="bg-purple-600 hover:bg-purple-500 text-white"
                data-testid={`reply-submit-${post.post_id}`}
              >
                {posting ? '…' : 'Reply'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const RosterStrip = ({ crew, adminIds = [], currentUserId }) => {
  // iter_203 — Compact mobile roster header. Shows count + first 5
  // avatars + Admins badge, expands into full list on tap. Only rendered
  // when the viewer can see roster data (backend already gates this via
  // the crew payload — private crew non-members receive `members: []`).
  const [open, setOpen] = useState(false);
  const members = crew?.members || [];
  const totalCount = crew?.member_count || members.length;
  const avatars = members.slice(0, 5);
  const admins = members.filter((m) => adminIds.includes(m.user_id));
  return (
    <div
      className="rounded-xl border border-white/10 bg-white/[0.04] px-3 py-2"
      data-testid="roster-strip"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2"
        data-testid="roster-strip-toggle"
      >
        <span className="text-lg leading-none">👥</span>
        <div className="flex -space-x-2 mr-1">
          {avatars.map((m) => (
            <img
              key={m.user_id}
              src={getProfilePhotoUrl(m)}
              alt={m.artist_name}
              className="w-6 h-6 rounded-full object-cover border border-black"
              loading="lazy"
            />
          ))}
        </div>
        <span className="text-white text-sm font-black">
          {totalCount} member{totalCount === 1 ? "" : "s"}
        </span>
        {admins.length > 0 && (
          <span
            className="ml-1 px-1.5 py-0.5 rounded-full bg-amber-500/20 border border-amber-500/40 text-amber-100 text-[10px] font-black"
            data-testid="roster-strip-admin-count"
          >
            🛡️ {admins.length} admin{admins.length === 1 ? "" : "s"}
          </span>
        )}
        <span className="ml-auto text-xs text-gray-400">{open ? "Hide" : "View all"}</span>
      </button>
      {open && members.length > 0 && (
        <div className="mt-3 grid grid-cols-2 gap-1.5" data-testid="roster-strip-list">
          {members.map((m) => (
            <Link
              key={m.user_id}
              to={`/artist/${m.user_id}`}
              className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-purple-500/10"
              data-testid={`roster-strip-member-${m.user_id}`}
            >
              <img
                src={getProfilePhotoUrl(m)}
                alt={m.artist_name}
                className="w-7 h-7 rounded-full object-cover"
                loading="lazy"
              />
              <span className="text-white text-xs truncate flex-1">
                {m.artist_name}
                {m.user_id === currentUserId && <span className="text-gray-500"> · you</span>}
              </span>
              {m.user_id === crew?.creator_id && (
                <span className="text-[10px] font-black text-amber-300">creator</span>
              )}
              {m.user_id !== crew?.creator_id && adminIds.includes(m.user_id) && (
                <span className="text-[10px] font-black text-purple-300">admin</span>
              )}
            </Link>
          ))}
        </div>
      )}
      {open && members.length === 0 && (
        <p className="mt-2 text-xs text-gray-500 italic">
          Roster is hidden — join this community to see who&apos;s inside.
        </p>
      )}
    </div>
  );
};

const CrewDetailPage = () => {
  const { crewId } = useParams();
  const [searchParams] = useSearchParams();
  const inviteCode = searchParams.get('invite');
  const refCode = searchParams.get('ref');
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [crew, setCrew] = useState(null);
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [postText, setPostText] = useState('');
  const [postMentions, setPostMentions] = useState([]);
  const [shareTrackId, setShareTrackId] = useState('');
  const [postAsAnnouncement, setPostAsAnnouncement] = useState(false);
  const [myTracks, setMyTracks] = useState([]);
  const [posting, setPosting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteUrl, setInviteUrl] = useState('');
  const [inviteRefMode, setInviteRefMode] = useState('personal'); // 'personal' | 'crew' | 'none'
  const [inviteAttempted, setInviteAttempted] = useState(false);
  const [showSharePicker, setShowSharePicker] = useState(false);
  const [myRefCode, setMyRefCode] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({ name: '', description: '', category: '', genre: '', visibility: 'public' });
  const [savingEdit, setSavingEdit] = useState(false);
  const [adminOpUserId, setAdminOpUserId] = useState(null);  // disable double-click during promote/demote
  // Phase 19.0 — composer attachments
  const [postImageUrl, setPostImageUrl] = useState('');
  // Phase 21.4 Batch 9 — per-user collapse of pinned announcements.
  // Persisted in localStorage per crew so a user's "Hide pinned" choice
  // survives page reloads without touching the backend.
  const collapseKey = `rgm.crew.${crewId}.pinnedCollapsed`;
  const [pinnedCollapsed, setPinnedCollapsed] = useState(() => {
    try { return localStorage.getItem(collapseKey) === '1'; }
    catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(collapseKey, pinnedCollapsed ? '1' : '0'); }
    catch { /* ignore */ }
  }, [collapseKey, pinnedCollapsed]);
  const [postImageUploading, setPostImageUploading] = useState(false);
  const [shareReportId, setShareReportId] = useState('');
  const [reportsList, setReportsList] = useState([]);
  const coverInputRef = useRef(null);
  const postImageInputRef = useRef(null);

  const currentUserId = user?.user_id;
  const isMember = !!crew?.is_member;
  const isAdmin = !!crew?.is_admin;
  const isCreator = crew?.creator_id === currentUserId;
  const adminIds = crew?.admin_ids || [];
  const mutedIds = crew?.muted_ids || [];
  const isMuted = !!crew?.is_muted;
  const ADMIN_CAP = 4;
  // iter_196 — Moderation state
  const isPlatformAdmin = user?.role === "admin" || user?.role === "moderator";
  // iter_205 — Crew admins can now moderate members (kick + mute) alongside creator + platform admin.
  const canModerateMembers = isCreator || isAdmin || isPlatformAdmin;
  const canModerateCrew = isCreator || isPlatformAdmin;
  const isArchived = !!crew?.archived_at;
  const [moderationMode, setModerationMode] = useState(null); // "archive" | "restore" | "delete_permanent"
  const [memberOpUserId, setMemberOpUserId] = useState(null);

  useEffect(() => {
    api.get('/auth/me').then(r => setUser(r.data)).catch(() => setUser(null));
  }, []);

  const fetchCrew = async () => {
    try {
      const r = await api.get(`/crews/${crewId}`);
      setCrew(r.data);
      return r.data;
    } catch (err) {
      setError(err.response?.data?.detail || 'Could not load crew');
      return null;
    }
  };

  const fetchPosts = async () => {
    try {
      const r = await api.get(`/crews/${crewId}/posts`);
      setPosts(r.data.posts || []);
    } catch (err) {
      // private crew posts return 403; handled by error state on crew
    }
  };

  const fetchMyTracks = async () => {
    try {
      const r = await api.get('/submissions/my');
      setMyTracks((r.data || []).slice(0, 30));
    } catch {
      // optional — track sharing is gracefully degraded
    }
  };

  // Phase 19.0 — Load recent RGM Reports so admins can share them into the
  // community. Returns silently for non-admins / when the endpoint is empty.
  const fetchReports = async () => {
    try {
      const r = await api.get('/reports?limit=20');
      const list = Array.isArray(r.data) ? r.data : (r.data?.reports || []);
      setReportsList(list.slice(0, 20));
    } catch {
      setReportsList([]);
    }
  };

  // Phase 19.0 — Upload a single post image, returns Cloudinary CDN url
  const handlePostImageUpload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('Image must be 8MB or smaller'); return; }
    setPostImageUploading(true);
    try {
      const fd = new FormData();
      fd.append('image', file);
      const r = await api.post('/posts/upload-image', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPostImageUrl(r.data?.image_url || '');
      toast.success('Image attached');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Image upload failed');
    } finally {
      setPostImageUploading(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setError(null);
    Promise.all([fetchCrew(), fetchPosts()]).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [crewId]);

  // Phase 5 upgrade: invite link capture.
  // If this page was opened via /crews/{id}?invite=CODE:
  //   - logged in: try auto-join (even if crew fetch 403'd due to private crew)
  //   - not logged in: stash the invite so LoginPage/AuthCallback can apply it
  useEffect(() => {
    if (!inviteCode && !refCode) return;
    try {
      sessionStorage.setItem(
        'rgm_crew_invite',
        JSON.stringify({ crewId, code: inviteCode || null, ref: refCode || null })
      );
      // Phase 15.5 P5 Batch E — also stash the ref into the personal-referral
      // slot so /auth/google-session + /auth/apply-pending-referral attribute
      // the new signup to the inviting artist (not just the crew).
      if (refCode) {
        stashReferral(refCode);
      }
    } catch { /* ignore */ }
    if (!inviteCode) return;
    if (!user) return; // wait for auth
    if (crew?.is_member) return; // already a member, nothing to do
    if (inviteAttempted) return; // only try once per page visit
    setInviteAttempted(true);
    (async () => {
      try {
        const params = { invite: inviteCode };
        if (refCode) params.ref = refCode;
        const r = await api.post(`/crews/${crewId}/join`, null, { params });
        toast.success('Invite accepted — welcome!');
        if (r.data?.crew_seeder_awarded) {
          toast.success('🌱 A friend just earned Crew Seeder thanks to you!');
        }
        try { sessionStorage.removeItem('rgm_crew_invite'); } catch { /* ignore */ }
        setError(null); // clear prior "private" error if we were blocked
        await fetchCrew();
        await fetchPosts();
      } catch (err) {
        toast.error(err.response?.data?.detail || 'Invite could not be applied');
        // Drop the invite query param so the normal error screen can render
        // instead of the 'Applying your invite…' spinner hanging forever.
        navigate(`/crews/${crewId}`, { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inviteCode, refCode, user?.user_id, crew?.is_member, inviteAttempted]);

  useEffect(() => {
    if (user) fetchMyTracks();
  }, [user]);

  // Phase 19.0 — Reports are only loaded for admins (Share Report picker)
  useEffect(() => {
    if (user && crew?.is_admin) fetchReports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, crew?.is_admin]);

  const handleJoin = async () => {
    setBusy(true);
    try {
      const params = {};
      if (refCode) params.ref = refCode;
      await api.post(`/crews/${crewId}/join`, null, { params });
      toast.success('Joined!');
      await fetchCrew();
      await fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to join');
    } finally {
      setBusy(false);
    }
  };

  const handleRequestJoin = async () => {
    setBusy(true);
    try {
      const params = {};
      if (refCode) params.ref = refCode;
      const r = await api.post(`/crews/${crewId}/request-join`, null, { params });
      if (r.data.status === 'member') {
        toast.success('You are already a member');
      } else {
        toast.success('Request sent! Crew admins have been notified.');
      }
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send request');
    } finally {
      setBusy(false);
    }
  };

  const handleLeave = async () => {
    if (!window.confirm('Leave this crew?')) return;
    setBusy(true);
    try {
      await api.post(`/crews/${crewId}/leave`);
      toast.success('Left the crew');
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to leave');
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Delete this crew permanently? This cannot be undone.')) return;
    try {
      await api.delete(`/crews/${crewId}`);
      toast.success('Crew deleted');
      navigate('/crews');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const openEdit = () => {
    if (!crew) return;
    setEditForm({
      name: crew.name || '',
      description: crew.description || '',
      category: crew.category || '',
      genre: crew.genre || '',
      visibility: crew.visibility || 'public',
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async (e) => {
    e?.preventDefault?.();
    if (savingEdit) return;
    if (!editForm.name?.trim() || editForm.name.trim().length < 2) {
      toast.error('Name must be at least 2 characters');
      return;
    }
    setSavingEdit(true);
    try {
      const payload = {
        name: editForm.name.trim(),
        description: (editForm.description || '').trim(),
        category: editForm.category?.trim() || null,
        genre: editForm.genre?.trim() || null,
        visibility: editForm.visibility || 'public',
      };
      await api.patch(`/crews/${crewId}`, payload);
      toast.success('Crew updated');
      setShowEdit(false);
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update crew');
    } finally {
      setSavingEdit(false);
    }
  };

  // Phase 6 critical: multi-admin (max 4, creator-only)
  const handlePromoteAdmin = async (memberId, memberName) => {
    if (adminOpUserId) return;
    if (!isCreator) {
      toast.error('Only the creator can promote admins');
      return;
    }
    if (adminIds.length >= ADMIN_CAP) {
      toast.error(`Admin limit reached (${ADMIN_CAP}). Demote someone first.`);
      return;
    }
    setAdminOpUserId(memberId);
    try {
      await api.post(`/crews/${crewId}/admins/${memberId}`);
      toast.success(`${memberName || 'Member'} promoted to admin`);
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to promote');
    } finally {
      setAdminOpUserId(null);
    }
  };

  const handleDemoteAdmin = async (memberId, memberName) => {
    if (adminOpUserId) return;
    if (!isCreator) {
      toast.error('Only the creator can remove admins');
      return;
    }
    if (memberId === crew?.creator_id) {
      toast.error("The creator can't be demoted");
      return;
    }
    if (!window.confirm(`Remove ${memberName || 'this admin'} from admins?`)) return;
    setAdminOpUserId(memberId);
    try {
      await api.delete(`/crews/${crewId}/admins/${memberId}`);
      toast.success(`${memberName || 'Member'} removed from admins`);
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to demote');
    } finally {
      setAdminOpUserId(null);
    }
  };

  // iter_205 — Member moderation actions (kick / mute / unmute).
  // Available to creator + crew admin + platform admin. Backend re-checks
  // creator-only rules on admin↔admin operations.
  const handleKickMember = async (memberId, memberName) => {
    if (memberOpUserId) return;
    if (!canModerateMembers) return;
    if (memberId === crew?.creator_id) {
      toast.error("The creator can't be removed from their own crew");
      return;
    }
    if (!window.confirm(`Remove ${memberName || 'this member'} from the crew? They can request to rejoin later.`)) return;
    setMemberOpUserId(memberId);
    try {
      await api.post(`/crews/${crewId}/members/${memberId}/kick`);
      toast.success(`${memberName || 'Member'} removed`);
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to remove');
    } finally {
      setMemberOpUserId(null);
    }
  };

  const handleMuteMember = async (memberId, memberName, mute = true) => {
    if (memberOpUserId) return;
    if (!canModerateMembers) return;
    if (memberId === crew?.creator_id) {
      toast.error("The creator can't be muted");
      return;
    }
    const verb = mute ? 'Mute' : 'Unmute';
    if (!window.confirm(`${verb} ${memberName || 'this member'}? They will ${mute ? 'no longer be able to post or reply' : 'be able to post again'}.`)) return;
    setMemberOpUserId(memberId);
    try {
      await api.post(`/crews/${crewId}/members/${memberId}/${mute ? 'mute' : 'unmute'}`);
      toast.success(`${memberName || 'Member'} ${mute ? 'muted' : 'unmuted'}`);
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Failed to ${verb.toLowerCase()}`);
    } finally {
      setMemberOpUserId(null);
    }
  };

  const handlePost = async () => {
    if (!postText.trim() && !shareTrackId && !postImageUrl && !shareReportId) {
      toast.error('Add some text, an image, or share a track / report');
      return;
    }
    setPosting(true);
    try {
      await api.post(`/crews/${crewId}/posts`, {
        content: postText.trim(),
        shared_track_id: shareTrackId || null,
        is_announcement: postAsAnnouncement && isAdmin ? true : false,
        image_url: postImageUrl || null,
        shared_report_id: shareReportId || null,
        mention_user_ids: postMentions,
      });
      setPostText('');
      setPostMentions([]);
      setShareTrackId('');
      setPostAsAnnouncement(false);
      setPostImageUrl('');
      setShareReportId('');
      await fetchPosts();
      if (postAsAnnouncement && isAdmin) {
        toast.success('Announcement sent to all members');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post');
    } finally {
      setPosting(false);
    }
  };

  const handleReact = async (postId, emoji) => {
    try {
      const r = await api.post(`/crews/${crewId}/posts/${postId}/react`, { emoji });
      // Update local state
      setPosts((prev) => prev.map((p) =>
        p.post_id === postId ? { ...p, reactions: r.data.reactions } : p
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reaction failed');
    }
  };

  const handleReply = async (parentId, content, mentions = []) => {
    try {
      await api.post(`/crews/${crewId}/posts`, {
        content,
        parent_post_id: parentId,
        mention_user_ids: mentions,
      });
      await fetchPosts();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Reply failed');
    }
  };

  const handleDeletePost = async (postId) => {
    // iter_203 — Context-aware confirm. Moderator deleting SOMEONE
    // ELSE'S post gets a stronger prompt that mentions restore.
    const target = (posts || []).find((p) => p.post_id === postId)
      || (posts || []).flatMap((p) => p.replies || []).find((r) => r.post_id === postId);
    const isOwnPost = target?.author?.user_id === currentUserId;
    const msg = isOwnPost
      ? 'Remove this post? It will be replaced with a "Message removed by moderator" tombstone.'
      : 'Remove this entire post and keep its replies for possible restoration?';
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/crews/${crewId}/posts/${postId}`);
      await fetchPosts();
      toast.success('Post removed');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  const handleRestorePost = async (postId) => {
    // iter_203 — Restore a moderator-deleted post.
    if (!window.confirm('Restore this post? Its original content and any replies will reappear for everyone in the crew.')) return;
    try {
      await api.post(`/crews/${crewId}/posts/${postId}/restore`);
      await fetchPosts();
      toast.success('Post restored');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to restore');
    }
  };

  const handleOpenInvite = async (withRef) => {
    try {
      // Buttons can call us with the click event as the first arg — guard
      // against that so we don't forward a SyntheticEvent to the backend
      // (which would fail Pydantic validation and surface "Could not create
      // invite link" instead of the real reason). Only honour string values.
      const safeWithRef = typeof withRef === "string" ? withRef : null;
      const refMode = safeWithRef || inviteRefMode || 'personal';
      const r = await api.post(`/crews/${crewId}/invite-link`, { with_ref: refMode });
      const origin = getPublicAppUrl();
      setInviteUrl(`${origin}${r.data.path}`);
      setInviteRefMode(refMode);
      setShowInviteDialog(true);
    } catch (err) {
      const d = err?.response?.data?.detail;
      const status = err?.response?.status;
      // Phase 19.0.1 — Pull a human-readable message out of *any* detail
      // shape (string, array of validation errors, object with .msg). The
      // old fallback hid the real cause behind "Could not create invite link".
      let detailMsg = null;
      if (typeof d === "string") detailMsg = d;
      else if (Array.isArray(d) && d[0]?.msg) detailMsg = d[0].msg;
      else if (d && typeof d === "object" && d.msg) detailMsg = d.msg;
      const msg = detailMsg
        ? detailMsg
        : status === 401
          ? 'You need to log in to share this crew.'
          : status === 403
            ? 'Only members of this crew can share its invite link.'
            : status === 404
              ? 'This crew no longer exists.'
              : `Could not create invite link${status ? ` (HTTP ${status})` : ''} — please try again.`;
      toast.error(msg);
    }
  };

  const handleChangeInviteRefMode = async (mode) => {
    try {
      const r = await api.post(`/crews/${crewId}/invite-link`, { with_ref: mode });
      const origin = getPublicAppUrl();
      setInviteUrl(`${origin}${r.data.path}`);
      setInviteRefMode(mode);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not update invite link');
    }
  };

  const ensureMyRefCode = async () => {
    if (myRefCode) return myRefCode;
    if (!user) return null;
    try {
      const r = await api.get('/referral/my-code');
      const code = r.data?.referral_code || null;
      setMyRefCode(code);
      return code;
    } catch {
      return null;
    }
  };

  const buildShareUrl = (ref) => {
    const base = getPublicShareUrl(`/api/share/crew/${crewId}`);
    return ref ? `${base}?ref=${encodeURIComponent(ref)}` : base;
  };

  const isMobile = () => {
    if (typeof navigator === 'undefined') return false;
    return /Android|iPhone|iPad|iPod|Mobi/i.test(navigator.userAgent || '');
  };

  const handleShareCrew = async () => {
    const ref = await ensureMyRefCode();
    const shareUrl = buildShareUrl(ref);
    const shareText = `Join ${crew?.name || 'my crew'} on RGM Studio`;

    // Mobile: use native share sheet
    if (isMobile() && navigator.share) {
      try {
        await navigator.share({
          title: crew?.name || 'Crew on RGM Studio',
          text: shareText,
          url: shareUrl,
        });
        toast.success("Invite sent — you'll earn Crew Seeder status after 3 joins");
        return;
      } catch (err) {
        if (err?.name === 'AbortError') return; // user cancelled
        // fall through to picker
      }
    }
    // Desktop: open picker with X / WhatsApp / Copy Link
    setShowSharePicker(true);
  };

  const handleSharePick = async (channel) => {
    const ref = await ensureMyRefCode();
    const shareUrl = buildShareUrl(ref);
    const shareText = `Join ${crew?.name || 'my crew'} on RGM Studio`;
    const encodedText = encodeURIComponent(shareText);
    const encodedUrl = encodeURIComponent(shareUrl);

    if (channel === 'twitter') {
      window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`, '_blank', 'noopener');
    } else if (channel === 'whatsapp') {
      window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`, '_blank', 'noopener');
    } else if (channel === 'copy') {
      try {
        await navigator.clipboard.writeText(shareUrl);
        toast.success('Link copied!');
      } catch {
        toast.error('Could not copy link');
      }
    }
    toast.success("Invite sent — you'll earn Crew Seeder status after 3 joins");
    setShowSharePicker(false);
  };

  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Max cover size is 5MB');
      return;
    }
    const fd = new FormData();
    fd.append('cover', file);
    try {
      await api.post(`/crews/${crewId}/cover`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Cover updated!');
      await fetchCrew();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Cover upload failed');
    }
  };

  // Shared tracks (deduped from posts)
  const sharedTracks = (() => {
    const seen = new Set();
    const list = [];
    posts.forEach((p) => {
      if (p.shared_track && !seen.has(p.shared_track.submission_id)) {
        seen.add(p.shared_track.submission_id);
        list.push(p.shared_track);
      }
    });
    return list;
  })();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#08080c]">
        <SharedNavigation currentUser={user} activePage="crews" />
        <div className="pt-24 flex items-center justify-center h-[calc(100vh-6rem)]">
          <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500"></div>
        </div>
      </div>
    );
  }

  if (error || !crew) {
    // If we have a pending invite code + an authenticated user, suppress the
    // error screen — the invite effect will auto-join and re-fetch shortly.
    if (inviteCode && user && !crew) {
      return (
        <div className="min-h-screen bg-[#08080c]">
          <SharedNavigation currentUser={user} activePage="crews" />
          <div className="pt-24 flex items-center justify-center h-[calc(100vh-6rem)]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-purple-500 mx-auto"></div>
              <p className="text-gray-400 text-sm mt-4">Applying your invite…</p>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#08080c]" data-testid="crew-error">
        <SharedNavigation currentUser={user} activePage="crews" />
        <div className="pt-24 max-w-2xl mx-auto px-4 text-center">
          <p className="text-5xl mb-3">😕</p>
          <h2 className="text-white text-2xl font-bold mb-2">{error || 'Crew not found'}</h2>
          <Link to="/crews">
            <Button className="bg-purple-600 hover:bg-purple-500 text-white mt-4">Back to Crews</Button>
          </Link>
        </div>
      </div>
    );
  }

  const coverUrl = getPlaylistCoverUrl({
    cover_image: crew.cover_image,
    cover_updated_at: crew.cover_updated_at,
  });

  return (
    <div className="min-h-screen bg-[#08080c]" data-testid="crew-detail-page">
      <SharedNavigation currentUser={user} activePage="crews" />

      {/* Header */}
      {/* Phase 19.1.1 — pt-24 (instead of pt-16) so the Back button + Share
          row aren't hidden under both the SharedNavigation (h-16) AND the
          StripeHealthBanner (~50px) which can both be fixed at the top in
          production. */}
      <div className="relative pt-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-3">
          <BackButton
            fallback={crew?.kind === "group" ? "/groups" : crew?.kind === "club" ? "/clubs" : "/crews"}
            testId="crew-back-button"
            alwaysFallback
          />
        </div>
        <div className="aspect-[16/6] sm:aspect-[16/5] w-full overflow-hidden">
          <img
            src={coverUrl}
            alt={crew.name}
            className="w-full h-full object-cover"
            onError={(e) => { if (!e.target.dataset.fellback) { e.target.dataset.fellback = "1"; e.target.src = "/rgm-logo.png"; } }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] via-transparent to-transparent"></div>
        </div>

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 -mt-16 relative">
          {/* iter_196 — Archived banner (owner + admins only see the crew
              when archived; this banner tells them why + offers Restore). */}
          {isArchived && (
            <div
              className="mb-3 rounded-lg border border-amber-500/40 bg-amber-500/10 text-amber-100 px-4 py-2 text-sm flex items-center gap-2"
              data-testid="crew-archived-banner"
            >
              <span className="text-lg" aria-hidden>📦</span>
              <span className="flex-1">
                This community is <strong>archived</strong> — hidden from public listings.
                {crew.archived_reason ? ` Reason: ${crew.archived_reason}.` : ""}
              </span>
              {canModerateCrew && (
                <Button
                  size="sm"
                  onClick={() => setModerationMode("restore")}
                  className="bg-emerald-500 hover:bg-emerald-600 text-white h-7"
                  data-testid="crew-archived-banner-restore"
                >
                  ↺ Restore
                </Button>
              )}
            </div>
          )}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <span className="text-xs px-2 py-0.5 rounded-full border bg-purple-600/30 text-purple-200 border-purple-500/40">
                  {crew.kind === 'group' ? '👥 Group' : crew.kind === 'club' ? '🎵 Club' : '🤝 Crew'}
                </span>
                {crew.visibility === 'private' && (
                  <span className="text-xs px-2 py-0.5 rounded-full border border-gray-500/40 bg-gray-700/60 text-gray-200">
                    🔒 Private
                  </span>
                )}
                {crew.genre && (
                  <span className="text-xs px-2 py-0.5 rounded-full bg-pink-600/30 text-pink-200 border border-pink-500/40">
                    {crew.genre}
                  </span>
                )}
              </div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{crew.name}</h1>
              <p className="text-gray-300 text-sm mt-1">
                👥 {crew.member_count} {crew.member_count === 1 ? 'member' : 'members'} · {crew.post_count || 0} posts
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={handleShareCrew}
                variant="outline"
                className="text-white border-gray-700"
                data-testid="crew-share-button"
              >
                Share
              </Button>
              {isAdmin && (
                <>
                  <input
                    ref={coverInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    onChange={handleCoverUpload}
                    className="hidden"
                    data-testid="crew-cover-input"
                  />
                  <Button
                    onClick={() => coverInputRef.current?.click()}
                    variant="outline"
                    className="text-white border-gray-700"
                    data-testid="crew-change-cover"
                  >
                    Change Cover
                  </Button>
                  <Button
                    onClick={openEdit}
                    variant="outline"
                    className="text-white border-gray-700"
                    data-testid="crew-edit-button"
                  >
                    ✏️ Edit
                  </Button>
                  <Button
                    onClick={handleOpenInvite}
                    variant="outline"
                    className="text-white border-purple-500/40 hover:bg-purple-500/10"
                    data-testid="invite-artists-button"
                  >
                    + Invite Artists
                  </Button>
                </>
              )}
              {!user ? (
                <Link to="/login">
                  <Button className="bg-purple-600 hover:bg-purple-500 text-white">Sign in to join</Button>
                </Link>
              ) : isMember ? (
                <>
                  {/* iter_196 — Archive / Restore / Delete Permanently
                      buttons. Visible only to community owner OR platform
                      admin. Regular members see the Leave button instead. */}
                  {canModerateCrew ? (
                    <div className="flex flex-wrap items-center gap-2" data-testid="crew-moderation-controls">
                      {isArchived ? (
                        <Button
                          onClick={() => setModerationMode("restore")}
                          variant="outline"
                          className="text-emerald-200 border-emerald-500/40 hover:bg-emerald-500/20"
                          data-testid="crew-restore-btn"
                        >
                          ↺ Restore
                        </Button>
                      ) : (
                        <Button
                          onClick={() => setModerationMode("archive")}
                          variant="outline"
                          className="text-amber-200 border-amber-500/40 hover:bg-amber-500/20"
                          data-testid="crew-archive-btn"
                        >
                          📦 Archive
                        </Button>
                      )}
                      <Button
                        onClick={() => setModerationMode("delete_permanent")}
                        variant="outline"
                        className="text-rose-300 border-rose-500/40 hover:bg-rose-500/20"
                        data-testid="crew-delete-permanent-btn"
                      >
                        🗑️ Delete permanently
                      </Button>
                    </div>
                  ) : (
                    <Button
                      onClick={handleLeave}
                      disabled={busy}
                      variant="outline"
                      className="text-white border-gray-700"
                      data-testid="crew-leave-button"
                    >
                      Leave
                    </Button>
                  )}
                </>
              ) : crew.visibility === 'invite_only' ? (
                <Button
                  onClick={crew.has_pending_invite ? handleJoin : undefined}
                  disabled={!crew.has_pending_invite || busy}
                  className={crew.has_pending_invite
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'
                    : 'bg-gray-800 text-gray-400 cursor-not-allowed'}
                  title={crew.has_pending_invite ? 'Accept your invite' : 'This crew is invite-only — ask an admin to invite you'}
                  data-testid="crew-join-button"
                >
                  {busy
                    ? 'Working…'
                    : crew.has_pending_invite
                      ? 'Accept Invite'
                      : '🔒 Invite-only'}
                </Button>
              ) : (
                <Button
                  onClick={crew.visibility === 'private'
                    ? (crew.has_pending_invite ? handleJoin : handleRequestJoin)
                    : handleJoin}
                  disabled={busy || crew.has_pending_request}
                  className={crew.has_pending_request
                    ? 'bg-gray-700 text-gray-300 cursor-not-allowed'
                    : 'bg-gradient-to-r from-purple-600 to-pink-600 text-white'}
                  data-testid="crew-join-button"
                >
                  {busy
                    ? 'Working…'
                    : crew.has_pending_request
                      ? 'Request Pending'
                      : crew.visibility === 'private'
                        ? (crew.has_pending_invite ? 'Accept Invite' : 'Request to Join')
                        : 'Join Crew'}
                </Button>
              )}
            </div>
          </div>

          {crew.description && (
            <Card className="mt-6 p-4 bg-gray-900/50 border-gray-800">
              <p className="text-gray-200 whitespace-pre-wrap">{crew.description}</p>
            </Card>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-16 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Posts column */}
        <div className="lg:col-span-2 space-y-4">
          {/* iter_203 — Compact roster strip for mobile.
              Desktop keeps the richer sidebar card in place; on mobile
              this strip surfaces roster + admin count above the feed
              so the hierarchy is visible without scrolling to the
              bottom. */}
          <div className="lg:hidden" data-testid="roster-strip-mobile">
            <RosterStrip
              crew={crew}
              adminIds={adminIds}
              currentUserId={currentUserId}
            />
          </div>
          {/* Composer */}
          {isMember && isMuted && (
            <Card
              className="p-4 bg-rose-500/5 border-rose-500/30 flex items-center gap-2"
              data-testid="post-composer-muted"
            >
              <span className="text-lg">🔇</span>
              <p className="text-rose-100 text-sm">
                You are muted in this crew. You can still view + react, but posting and replies are disabled until a moderator unmutes you.
              </p>
            </Card>
          )}
          {isMember && !isMuted && (
            <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="post-composer">
              <MentionTextarea
                value={postText}
                onChange={setPostText}
                onMentionsChange={setPostMentions}
                rows={3}
                placeholder="Share something with the crew… type @ to mention"
                className="bg-black/30 text-white border-gray-700"
                testIdPrefix="post-input"
              />
              {myTracks.length > 0 && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Label className="text-xs text-gray-400">Share a track:</Label>
                  <select
                    value={shareTrackId}
                    onChange={(e) => setShareTrackId(e.target.value)}
                    className="text-sm px-2 py-1 rounded-md bg-black/40 text-white border border-gray-700"
                    data-testid="share-track-select"
                  >
                    <option value="">— none —</option>
                    {myTracks.map((t) => (
                      <option key={t.submission_id} value={t.submission_id}>
                        {t.track_title}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {/* Phase 19.0 — Attach image to post */}
              <div className="mt-2 flex items-center gap-2 flex-wrap">
                <input
                  ref={postImageInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handlePostImageUpload}
                  className="hidden"
                  data-testid="post-image-input"
                />
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => postImageInputRef.current?.click()}
                  disabled={postImageUploading}
                  className="text-xs text-gray-200 border-gray-700"
                  data-testid="post-image-attach"
                >
                  {postImageUploading ? 'Uploading…' : (postImageUrl ? '🖼️ Replace image' : '🖼️ Attach image')}
                </Button>
                {postImageUrl && (
                  <>
                    <img
                      src={postImageUrl}
                      alt="attachment preview"
                      className="h-12 w-12 rounded object-cover border border-gray-700"
                      data-testid="post-image-preview"
                    />
                    <button
                      type="button"
                      onClick={() => setPostImageUrl('')}
                      className="text-xs text-red-300 hover:text-red-200"
                      data-testid="post-image-clear"
                    >Remove</button>
                  </>
                )}
              </div>
              {/* Phase 19.0 — Admins/A&R can share an RGM Report into the community */}
              {isAdmin && reportsList.length > 0 && (
                <div className="mt-2 flex items-center gap-2 flex-wrap">
                  <Label className="text-xs text-gray-400">📰 Share RGM Report:</Label>
                  <select
                    value={shareReportId}
                    onChange={(e) => setShareReportId(e.target.value)}
                    className="text-sm px-2 py-1 rounded-md bg-black/40 text-white border border-gray-700 max-w-xs truncate"
                    data-testid="share-report-select"
                  >
                    <option value="">— none —</option>
                    {reportsList.map((r) => (
                      <option key={r.report_id} value={r.report_id}>
                        {(r.title || 'Untitled report').slice(0, 60)}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex justify-between items-center mt-2 gap-2 flex-wrap">
                {isAdmin ? (
                  <label className="flex items-center gap-2 text-xs text-gray-300 cursor-pointer select-none" data-testid="announcement-toggle-label">
                    <input
                      type="checkbox"
                      checked={postAsAnnouncement}
                      onChange={(e) => setPostAsAnnouncement(e.target.checked)}
                      className="w-4 h-4 accent-fuchsia-500"
                      data-testid="announcement-toggle"
                    />
                    <span>📣 Post as announcement</span>
                    <span className="text-gray-500">(notifies all members)</span>
                  </label>
                ) : <span />}
                <Button
                  onClick={handlePost}
                  disabled={posting}
                  className={postAsAnnouncement && isAdmin
                    ? "bg-gradient-to-r from-fuchsia-600 to-pink-600 hover:from-fuchsia-500 hover:to-pink-500 text-white"
                    : "bg-purple-600 hover:bg-purple-500 text-white"
                  }
                  data-testid="submit-post-button"
                >
                  {posting ? 'Posting…' : (postAsAnnouncement && isAdmin ? 'Send Announcement' : 'Post')}
                </Button>
              </div>
            </Card>
          )}
          {!isMember && user && (
            <Card className="p-4 bg-gray-900/40 border-gray-800 text-center text-sm text-gray-300">
              Join this {crew.kind} to post and react.
            </Card>
          )}

          {posts.length === 0 ? (
            <div className="text-center py-12 text-gray-400" data-testid="posts-empty">
              <p className="text-3xl mb-2">💭</p>
              <p>No posts yet. {isMember ? 'Be the first to share!' : 'Check back soon.'}</p>
            </div>
          ) : (
            (() => {
              // Phase 21.4 Batch 9 — split pinned announcements from
              // regular posts so multiple pins don't stack full-size.
              const pinned = posts.filter((p) => p.is_announcement);
              const regular = posts.filter((p) => !p.is_announcement);
              return (
                <>
                  {pinned.length > 0 && pinnedCollapsed && (
                    <div
                      className="flex items-center justify-between p-3 rounded-lg border border-fuchsia-500/40 bg-fuchsia-900/20"
                      data-testid="pinned-collapsed-bar"
                    >
                      <p className="text-xs font-semibold text-fuchsia-200">
                        📌 {pinned.length} pinned update{pinned.length === 1 ? '' : 's'} hidden
                      </p>
                      <button
                        type="button"
                        onClick={() => setPinnedCollapsed(false)}
                        className="text-[11px] font-black text-fuchsia-100 hover:text-white underline"
                        data-testid="pinned-show-btn"
                      >
                        Show
                      </button>
                    </div>
                  )}
                  {pinned.length > 0 && !pinnedCollapsed && (
                    <div className="space-y-2" data-testid="pinned-posts-section">
                      <div className="flex items-center justify-between px-1">
                        <p className="text-[11px] uppercase tracking-widest text-fuchsia-300 font-black">
                          📌 Pinned updates ({pinned.length})
                        </p>
                        <button
                          type="button"
                          onClick={() => setPinnedCollapsed(true)}
                          className="text-[11px] font-black text-fuchsia-200 hover:text-white underline"
                          data-testid="pinned-hide-btn"
                        >
                          Hide
                        </button>
                      </div>
                      {pinned.map((p) => (
                        <PostBlock
                          key={p.post_id}
                          post={p}
                          currentUserId={currentUserId}
                          isMember={isMember}
                          onReact={handleReact}
                          onReply={handleReply}
                          onDelete={handleDeletePost}
                          onRestore={handleRestorePost}
                          crewMeta={{ creatorId: crew?.creator_id, adminIds, isPlatformAdmin }}
                        />
                      ))}
                    </div>
                  )}
                  {regular.map((p) => (
                    <PostBlock
                      key={p.post_id}
                      post={p}
                      currentUserId={currentUserId}
                      isMember={isMember}
                      onReact={handleReact}
                      onReply={handleReply}
                      onDelete={handleDeletePost}
                      onRestore={handleRestorePost}
                      crewMeta={{ creatorId: crew?.creator_id, adminIds, isPlatformAdmin }}
                    />
                  ))}
                </>
              );
            })()
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Admin: Join Requests */}
          {isAdmin && crew.visibility === 'private' && (
            <CrewRequestsPanel crewId={crewId} onChange={fetchCrew} />
          )}
          {/* Phase 6 Critical: Admin Management (creator-only) */}
          {isCreator && (
            <Card
              className="p-4 bg-purple-500/5 border-purple-500/30"
              data-testid="crew-admin-management"
            >
              <div className="flex items-baseline justify-between mb-3 flex-wrap gap-2">
                <h3 className="text-white font-bold flex items-center gap-2">
                  <span>🛡️</span> Admins
                  <span
                    className="text-xs font-normal text-purple-200/70"
                    data-testid="crew-admin-count"
                  >
                    {adminIds.length} / {ADMIN_CAP}
                  </span>
                </h3>
                <p className="text-[11px] text-gray-400 max-w-[280px] text-right">
                  You're the creator. You can promote up to {ADMIN_CAP} admins for this {crew?.kind === 'group' ? 'group' : 'crew'}.
                </p>
              </div>

              {/* Current admin chips */}
              <div className="flex flex-wrap gap-2 mb-4" data-testid="crew-admin-chips">
                {adminIds.map((aid) => {
                  const m = (crew?.members || []).find((x) => x.user_id === aid) || { user_id: aid, artist_name: aid };
                  const isTheCreator = aid === crew?.creator_id;
                  return (
                    <div
                      key={aid}
                      className={`flex items-center gap-2 px-2 py-1 rounded-full border text-xs ${
                        isTheCreator
                          ? 'bg-amber-500/15 border-amber-500/40 text-amber-100'
                          : 'bg-purple-500/15 border-purple-500/40 text-purple-100'
                      }`}
                      data-testid={`crew-admin-chip-${aid}`}
                    >
                      <img
                        src={getProfilePhotoUrl(m)}
                        alt=""
                        className="w-5 h-5 rounded-full object-cover"
                      />
                      <span className="font-medium truncate max-w-[140px]">{m.artist_name || m.name || aid}</span>
                      {isTheCreator ? (
                        <span title="Creator" className="text-[10px] uppercase tracking-wider font-bold">creator</span>
                      ) : (
                        <button
                          type="button"
                          disabled={adminOpUserId === aid}
                          onClick={() => handleDemoteAdmin(aid, m.artist_name || m.name)}
                          className="ml-1 h-4 w-4 rounded-full bg-white/10 hover:bg-red-500/40 text-white/80 hover:text-white text-[10px] leading-none flex items-center justify-center disabled:opacity-50"
                          title="Remove admin"
                          data-testid={`crew-demote-${aid}`}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Promote-from-members list */}
              <div>
                <h4 className="text-xs uppercase tracking-wider text-gray-400 mb-2">Promote a member</h4>
                {(() => {
                  const promotable = (crew?.members || []).filter(
                    (m) => !adminIds.includes(m.user_id)
                  );
                  if (promotable.length === 0) {
                    return (
                      <p className="text-xs text-gray-500 italic">
                        No members available to promote. Invite more artists to grow your admin team.
                      </p>
                    );
                  }
                  const capReached = adminIds.length >= ADMIN_CAP;
                  return (
                    <div className="space-y-1.5 max-h-56 overflow-y-auto pr-1">
                      {promotable.map((m) => (
                        <div
                          key={m.user_id}
                          className="flex items-center justify-between gap-2 p-2 rounded-md bg-black/30 border border-gray-800"
                          data-testid={`crew-promotable-${m.user_id}`}
                        >
                          <div className="flex items-center gap-2 min-w-0">
                            <img
                              src={getProfilePhotoUrl(m)}
                              alt=""
                              className="w-7 h-7 rounded-full object-cover"
                            />
                            <span className="text-white text-sm truncate">{m.artist_name || m.name}</span>
                          </div>
                          <Button
                            size="sm"
                            disabled={capReached || adminOpUserId === m.user_id}
                            onClick={() => handlePromoteAdmin(m.user_id, m.artist_name || m.name)}
                            className="bg-purple-600 hover:bg-purple-700 text-white text-xs h-7 px-3 disabled:opacity-50"
                            title={capReached ? 'Admin cap reached' : 'Promote to admin'}
                            data-testid={`crew-promote-${m.user_id}`}
                          >
                            {adminOpUserId === m.user_id ? '…' : 'Promote'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </Card>
          )}

          {/* Members */}
          <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="crew-members">
            <h3 className="text-white font-bold mb-3 flex items-center gap-2">
              <span>👥</span> Members ({crew.member_count})
            </h3>
            <div className="grid grid-cols-2 gap-2">
              {(crew.members || []).map((m) => (
                <Link
                  key={m.user_id}
                  to={`/artist/${m.user_id}`}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-purple-500/10 transition-colors"
                  data-testid={`crew-member-${m.user_id}`}
                >
                  <img
                    src={getProfilePhotoUrl(m)}
                    alt={m.artist_name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <span className="text-white text-sm truncate flex-1">{m.artist_name}</span>
                  <PresenceDot userId={m.user_id} size="sm" />
                  {mutedIds.includes(m.user_id) && (
                    <span
                      className="text-[10px] font-black text-rose-300"
                      title="Muted"
                      data-testid={`crew-member-muted-${m.user_id}`}
                    >🔇</span>
                  )}
                  <CrewMemberBadges
                    memberBadges={m.badges || []}
                    isCreator={m.user_id === crew?.creator_id}
                    isAdmin={adminIds.includes(m.user_id)}
                    userId={m.user_id}
                  />
                </Link>
              ))}
            </div>
          </Card>

          {/* iter_205 — Member Moderation panel (creator + crew admin + platform admin) */}
          {canModerateMembers && (crew.members || []).length > 0 && (
            <Card
              className="p-4 bg-gray-900/40 border-gray-800"
              data-testid="crew-member-moderation"
            >
              <h3 className="text-white font-bold mb-1 flex items-center gap-2">
                <span>🛡️</span> Moderation
              </h3>
              <p className="text-[11px] text-gray-500 mb-3">
                Mute stops posting only — muted members can still view and react. Removed members can request to rejoin.
              </p>
              <div className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {(crew.members || [])
                  .filter((m) => m.user_id !== crew?.creator_id && m.user_id !== currentUserId)
                  .map((m) => {
                    const isMemberAdmin = adminIds.includes(m.user_id);
                    const isMemberMuted = mutedIds.includes(m.user_id);
                    // Crew admins can't act on other admins — creator/platform admin only
                    const disabledAdminOp = isMemberAdmin && !(isCreator || isPlatformAdmin);
                    return (
                      <div
                        key={m.user_id}
                        className="flex items-center justify-between gap-2 p-2 rounded-md bg-black/30 border border-gray-800"
                        data-testid={`crew-mod-row-${m.user_id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <img
                            src={getProfilePhotoUrl(m)}
                            alt=""
                            className="w-7 h-7 rounded-full object-cover"
                          />
                          <span className="text-white text-sm truncate">
                            {m.artist_name || m.name}
                            {isMemberAdmin && <span className="ml-1 text-[10px] font-black text-purple-300">admin</span>}
                            {isMemberMuted && <span className="ml-1 text-[10px] font-black text-rose-300">muted</span>}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 flex-shrink-0">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={memberOpUserId === m.user_id || disabledAdminOp}
                            onClick={() => handleMuteMember(m.user_id, m.artist_name || m.name, !isMemberMuted)}
                            className={`h-7 px-2 text-xs ${
                              isMemberMuted
                                ? "text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/10"
                                : "text-amber-300 border-amber-500/40 hover:bg-amber-500/10"
                            } disabled:opacity-40`}
                            title={disabledAdminOp ? "Only the creator can act on another admin" : (isMemberMuted ? "Restore posting" : "Mute posting")}
                            data-testid={`crew-mod-${isMemberMuted ? 'unmute' : 'mute'}-${m.user_id}`}
                          >
                            {memberOpUserId === m.user_id ? '…' : (isMemberMuted ? '🔊 Unmute' : '🔇 Mute')}
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={memberOpUserId === m.user_id || disabledAdminOp}
                            onClick={() => handleKickMember(m.user_id, m.artist_name || m.name)}
                            className="h-7 px-2 text-xs text-rose-300 border-rose-500/40 hover:bg-rose-500/10 disabled:opacity-40"
                            title={disabledAdminOp ? "Only the creator can remove another admin" : "Remove from crew"}
                            data-testid={`crew-mod-kick-${m.user_id}`}
                          >
                            {memberOpUserId === m.user_id ? '…' : '🚪 Remove'}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                {(crew.members || []).filter((m) => m.user_id !== crew?.creator_id && m.user_id !== currentUserId).length === 0 && (
                  <p className="text-xs text-gray-500 italic">
                    No other members to moderate yet.
                  </p>
                )}
              </div>
            </Card>
          )}

          {/* Shared tracks */}
          {sharedTracks.length > 0 && (
            <Card className="p-4 bg-gray-900/40 border-gray-800" data-testid="crew-shared-tracks">
              <h3 className="text-white font-bold mb-3 flex items-center gap-2">
                <span>🎵</span> Shared Tracks ({sharedTracks.length})
              </h3>
              <div className="space-y-2">
                {sharedTracks.map((t) => (
                  <Link
                    key={t.submission_id}
                    to={`/track/${t.submission_id}`}
                    className="flex items-center gap-3 p-2 rounded-lg hover:bg-purple-500/10 transition-colors"
                  >
                    <img
                      src={getCoverUrl(t)}
                      alt={t.track_title}
                      className="w-10 h-10 rounded object-cover"
                    />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm truncate">{t.track_title}</p>
                      <p className="text-gray-400 text-xs truncate">{t.artist_name}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          )}
        </div>
      </div>

      <InviteArtistsDialog
        open={showInviteDialog}
        onClose={() => setShowInviteDialog(false)}
        crewId={crewId}
        crewName={crew.name}
        inviteUrl={inviteUrl}
        refMode={inviteRefMode}
        onRefModeChange={handleChangeInviteRefMode}
        onInviteSent={() => fetchCrew()}
      />

      {/* Smart Share Picker (desktop) */}
      {showSharePicker && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => setShowSharePicker(false)}
          data-testid="share-picker-dialog"
        >
          <div
            className="w-full max-w-sm bg-[#161620] border border-purple-500/40 rounded-xl p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-white">Share {crew.name}</h3>
              <button
                onClick={() => setShowSharePicker(false)}
                className="text-gray-400 hover:text-white text-2xl"
                aria-label="Close"
              >×</button>
            </div>
            <p className="text-xs text-gray-400 mb-4">
              Bring 3+ artists in and unlock the <span className="text-green-300">🌱 Crew Seeder</span> badge.
            </p>
            <div className="grid grid-cols-3 gap-2 mb-3">
              <button
                onClick={() => handleSharePick('twitter')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg bg-black/40 border border-gray-800 hover:border-purple-500/40 transition-colors"
                data-testid="share-twitter"
              >
                <span className="text-2xl">𝕏</span>
                <span className="text-xs text-gray-300">X / Twitter</span>
              </button>
              <button
                onClick={() => handleSharePick('whatsapp')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg bg-black/40 border border-gray-800 hover:border-green-500/40 transition-colors"
                data-testid="share-whatsapp"
              >
                <span className="text-2xl">💬</span>
                <span className="text-xs text-gray-300">WhatsApp</span>
              </button>
              <button
                onClick={() => handleSharePick('copy')}
                className="flex flex-col items-center gap-1 p-3 rounded-lg bg-black/40 border border-gray-800 hover:border-blue-500/40 transition-colors"
                data-testid="share-copy"
              >
                <span className="text-2xl">🔗</span>
                <span className="text-xs text-gray-300">Copy link</span>
              </button>
            </div>
            <p className="text-[11px] text-gray-500">
              Links include your referral so every join counts toward your Crew Seeder status.
            </p>
          </div>
        </div>
      )}

      {/* Phase 6 Part 2: Edit crew modal (admins only) */}
      {showEdit && isAdmin && (
        <div
          className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
          onClick={() => !savingEdit && setShowEdit(false)}
          data-testid="crew-edit-dialog"
        >
          <div
            className="w-full max-w-lg bg-[#161620] border border-purple-500/40 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-white">✏️ Edit {crew?.kind === 'group' ? 'Group' : 'Crew'}</h2>
              <button
                onClick={() => !savingEdit && setShowEdit(false)}
                className="text-gray-400 hover:text-white text-2xl"
                data-testid="close-crew-edit"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-4">
              <div>
                <Label className="text-white block mb-1">Name *</Label>
                <input
                  value={editForm.name}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  maxLength={80}
                  required
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700 focus:border-purple-500 outline-none"
                  data-testid="crew-edit-name"
                />
              </div>
              <div>
                <Label className="text-white block mb-1">Description</Label>
                <textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((f) => ({ ...f, description: e.target.value }))}
                  maxLength={2000}
                  rows={3}
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700 focus:border-purple-500 outline-none"
                  data-testid="crew-edit-description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-white block mb-1">Category</Label>
                  <input
                    value={editForm.category}
                    onChange={(e) => setEditForm((f) => ({ ...f, category: e.target.value }))}
                    placeholder="e.g. Producers, Writers"
                    className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700 focus:border-purple-500 outline-none"
                    data-testid="crew-edit-category"
                  />
                </div>
                <div>
                  <Label className="text-white block mb-1">Genre</Label>
                  <input
                    value={editForm.genre}
                    onChange={(e) => setEditForm((f) => ({ ...f, genre: e.target.value }))}
                    placeholder="e.g. Hip Hop"
                    className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700 focus:border-purple-500 outline-none"
                    data-testid="crew-edit-genre"
                  />
                </div>
              </div>
              <div>
                <Label className="text-white block mb-1">Visibility</Label>
                <select
                  value={editForm.visibility}
                  onChange={(e) => setEditForm((f) => ({ ...f, visibility: e.target.value }))}
                  className="w-full px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
                  data-testid="crew-edit-visibility"
                >
                  <option value="public">🌐 Public — anyone can find and join</option>
                  <option value="private">🔒 Private — listed publicly, requires admin approval to join</option>
                  <option value="invite_only">🤫 Invite-only — hidden from search, joinable only via direct invite</option>
                </select>
                <div className="text-[11px] text-gray-500 mt-1 space-y-0.5">
                  {editForm.visibility === 'public' && <p>Anyone can see posts & members and join with one click.</p>}
                  {editForm.visibility === 'private' && <p>Crew appears in discovery with a slim preview. People must request to join; admins approve or reject.</p>}
                  {editForm.visibility === 'invite_only' && <p>Crew is hidden from discovery entirely. The only way in is a direct invite from a crew admin.</p>}
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Need to change the cover? Use the "Change Cover" button in the header.
              </p>
              <div className="flex gap-3 pt-2">
                <Button
                  type="submit"
                  disabled={savingEdit}
                  className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold disabled:opacity-60"
                  data-testid="crew-edit-save"
                >
                  {savingEdit ? 'Saving…' : 'Save changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => !savingEdit && setShowEdit(false)}
                  className="text-white border-gray-700"
                >
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* iter_196 — Community Moderation Modal (Archive / Restore / Delete Permanently) */}
      {crew && (
        <CrewModerationModal
          open={!!moderationMode}
          mode={moderationMode || "archive"}
          crew={crew}
          onClose={() => setModerationMode(null)}
          onDone={(mode) => {
            if (mode === "delete_permanent") {
              navigate("/crews");
            } else {
              // Refresh crew so the archived banner + button toggles update.
              fetchCrew();
            }
          }}
        />
      )}
    </div>
  );
};

// Tiny inline Label so we don't have to import another shadcn component
const Label = ({ children, className = '' }) => (
  <label className={`text-sm text-gray-300 ${className}`}>{children}</label>
);

export default CrewDetailPage;
